import type {LabelColorOptions} from '@primer/react'
import type {Environment, EnvironmentKind, EnvironmentStatus} from '@github-ui/agent-sessions-types/environment'
import {
  CodespacesIcon,
  ContainerIcon,
  CopilotIcon,
  DeviceDesktopIcon,
  WorkflowIcon,
  type Icon,
} from '@primer/octicons-react'

/**
 * Human-readable labels for each AHP environment operational status, shared by
 * every surface that displays an environment's status (the kickoff environment
 * picker and the task chat header).
 */
export const ENVIRONMENT_STATUS_LABELS: Record<EnvironmentStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  degraded: 'Degraded',
  waking: 'Waking',
  draining: 'Draining',
}

/**
 * Primer foreground color utility class for the status indicator dot shown next
 * to an environment's status. Only `online` is a healthy state; `offline` reads
 * as inactive (muted) while the transitional/reduced states use the attention
 * color.
 */
export const ENVIRONMENT_STATUS_DOT_COLOR_CLASS: Record<EnvironmentStatus, string> = {
  online: 'fgColor-success',
  offline: 'fgColor-muted',
  degraded: 'fgColor-attention',
  waking: 'fgColor-attention',
  draining: 'fgColor-attention',
}

/** Human-readable labels for each AHP environment compute kind. */
export const ENVIRONMENT_KIND_LABELS: Record<EnvironmentKind, string> = {
  'managed-actions': 'GitHub Actions',
  'managed-sandbox': 'GitHub sandbox',
  'managed-cca': 'Copilot cloud agent',
  'user-local': 'Local machine',
  'user-codespace': 'Codespace',
}

export const ENVIRONMENT_KIND_ICONS: Record<EnvironmentKind, Icon> = {
  'managed-actions': WorkflowIcon,
  'managed-sandbox': ContainerIcon,
  'managed-cca': CopilotIcon,
  'user-local': DeviceDesktopIcon,
  'user-codespace': CodespacesIcon,
}

/** Primer `Label` variant for each AHP environment operational status. */
export const ENVIRONMENT_STATUS_LABEL_VARIANTS: Record<EnvironmentStatus, LabelColorOptions> = {
  online: 'success',
  offline: 'secondary',
  degraded: 'attention',
  waking: 'attention',
  draining: 'attention',
}

/**
 * The environment name to show for a session. Managed sandboxes are allocated
 * per session, so their record name is an opaque id; user-managed hosts are
 * named by their owner and shown as-is.
 */
export function getEnvironmentDisplayName(environment: Environment): string {
  if (environment.kind === 'managed-sandbox') return 'Sandbox'
  if (environment.kind === 'user-local' || environment.kind === 'user-codespace') return environment.name
  return 'Cloud session'
}

/**
 * Statuses the environments query can still report while a host is already
 * answering on the relay: `offline` lags a successful wake, and `waking` is by
 * definition mid-transition. `degraded`/`draining` are not included — a live
 * relay does not contradict them.
 */
const STATUSES_SUPERSEDED_BY_LIVE_RELAY: ReadonlySet<EnvironmentStatus> = new Set(['offline', 'waking'])

export interface EnvironmentStatusDisplay {
  label: string
  variant: LabelColorOptions
}

/**
 * Reconciles the cached environment record with live relay reachability. The
 * environments query is polled, so it can still read `offline` while the relay
 * is connected and the session is steerable; the live connection wins there.
 */
export function resolveEnvironmentStatus(environment: Environment, relayReachable: boolean): EnvironmentStatusDisplay {
  const status =
    relayReachable && STATUSES_SUPERSEDED_BY_LIVE_RELAY.has(environment.status) ? 'online' : environment.status
  return {label: ENVIRONMENT_STATUS_LABELS[status], variant: ENVIRONMENT_STATUS_LABEL_VARIANTS[status]}
}

/**
 * AHP reports directories as `file://` URIs; show the bare filesystem path.
 * Keeps a UNC authority (`file://server/share`) and drops the leading slash
 * that precedes a Windows drive letter (`file:///C:/…`), so a non-POSIX host's
 * path is not silently rewritten.
 */
export function displayDirectory(directory: string): string {
  const FILE_SCHEME = 'file://'
  if (!directory.startsWith(FILE_SCHEME)) return directory

  const rest = directory.slice(FILE_SCHEME.length)
  const pathStart = rest.indexOf('/')
  // `file://host` with no path still names an authority worth keeping.
  if (pathStart === -1) return rest ? `//${rest}` : directory

  const authority = rest.slice(0, pathStart)
  const rawPath = rest.slice(pathStart)

  let path: string
  try {
    path = decodeURIComponent(rawPath)
  } catch {
    path = rawPath
  }

  if (authority) return `//${authority}${path}`
  return /^\/[a-zA-Z]:/.test(path) ? path.slice(1) : path
}
